import { LOADING_STATES } from '@/constants';
import {
    BRANCHING_NODES_TYPES,
    ENGAGEMENT_GOAL_TYPES,
    ERROR_MESSAGES,
    MESSAGE_DROPDOWN_OPTIONS,
    MESSAGE_TYPES,
} from '@/orchestrate/journeys/constants';
import {
    buildAudienceAndAudienceUiHint,
    buildJourney,
    cloneMessageGraph,
    extractMessagesFromMessageGraph,
    getAllGuidesIdsByJourney,
    getDropdownOptionsDescription,
    getDropdownOptionsDisabled,
    messagesGenerator,
    sanitizeMessageGraph,
    shouldAddOptionToMessageDropdown,
} from '@/orchestrate/journeys/helpers';
import Vue from 'vue';
import {
    addEngagementMessage,
    createJourney,
    deleteEngagementMessage,
    deleteJourney,
    getAllJourneys,
    getJourneyById,
    updateJourney,
    updateJourneyAudience,
    updateJourneyStatus,
    updateMessageDuration,
    updateMessageGraph,
    updateSchedule,
} from './journeys.utils';

import { isIntegratedEmail } from '@/emails/helpers';
import { createGuideFromLayout } from '@/guides/helpers/common.util';
import { CHANNEL_TYPES, PROVIDER_TYPES } from '@/orchestrate/journeys/integrations.constants';
import { STATUS_TYPES } from '@/orchestrate/journeys/status.constants';
import { loadEmailsByProvider } from '@/vuex/modules/emails/emails.utils';
import { getEmailType, isEmailTransactional } from '@pendo/services/EmailUtils';
import { getJourneyMessagesForJourneyLayout } from '@pendo/services/Journey';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import {
    initializeNodeIdMapping,
    processRegularNodes,
    setStartNodeIds,
    updateConditionNodes,
} from './journeys.helpers';
import { canEditGuideByAppId, canCreateGuideByAppId, canPublishGuide } from '@/vuex/modules/guides/guide-permissions.utils';
import { canUpdateEmail, canCreateEmail, canPublishEmail } from '@/vuex/modules/emails/emails-permissions.utils';

const { LOADING, RESOLVED, REJECTED, EMPTY } = LOADING_STATES;
const { GUIDE, EMAIL } = MESSAGE_TYPES;
const { AWS, HUBSPOT } = PROVIDER_TYPES;
const { PAUSED, DISABLED, ACTIVE, COMPLETED } = STATUS_TYPES;

let loadAllPromise;

const journeys = {
    namespaced: true,
    state: {
        map: {},
        currentJourneyId: null,
        currentEngagementId: null,
        currentMessageId: null,
        getAllJourneysStatus: LOADING,
        editing: false,
        saving: false,
        error: null,
        journeySegmentName: '',
        journeySegmentVisitorCount: 0,
        isCreatingTemplate: false,
        isCloningJourney: false,
        loadingMessages: false,
        integrationsByProvider: {},
        integrationError: {},
        journeyAnalyticsAggData: {},
    },
    mutations: {
        setMap (state, { journeysMap }) {
            state.map = journeysMap;
        },
        removeJourneysFromMaps (state, { id }) {
            Vue.delete(state.map, id);
        },
        addOrUpdateJourneyInMaps (state, { journey }) {
            Vue.set(state.map, journey.id, journey);
        },
        setGetAllJourneysStatus (state, { getAllJourneysStatus }) {
            state.getAllJourneysStatus = getAllJourneysStatus;
        },
        setEditing (state, { editing }) {
            state.editing = editing;
        },
        setSaving (state, { saving }) {
            state.saving = saving;
        },
        setError (state, { error }) {
            state.error = error;
        },
        setCurrentJourneyId (state, { id }) {
            state.currentJourneyId = id;
        },
        setCurrentEngagementId (state, { engagementId }) {
            state.currentEngagementId = engagementId;
        },
        setCurrentMessageId (state, { messageId }) {
            state.currentMessageId = messageId;
        },
        setEmailSettings (state, { emailSettings }) {
            const journey = state.map[state.currentJourneyId];
            if (!journey) return;

            Vue.set(journey, 'emailSettings', emailSettings);
        },
        updateEngagement (state, { engagement }) {
            const journey = state.map[state.currentJourneyId];
            if (!journey) return;

            const newEngagements = journey.engagements?.map((eng) => (eng.id === engagement?.id ? engagement : eng));
            Vue.set(journey, 'engagements', newEngagements);
        },
        setJourneySegmentVisitorCount (state, { count }) {
            state.journeySegmentVisitorCount = count;
        },
        setJourneySegmentName (state, { name }) {
            state.journeySegmentName = name;
        },
        setIsCreatingTemplate (state, { isCreatingTemplate }) {
            state.isCreatingTemplate = isCreatingTemplate;
        },
        setIsCloningJourney (state, { isCloningJourney }) {
            state.isCloningJourney = isCloningJourney;
        },
        setIntegrationsByProvider (state, { provider, list, channel }) {
            if (!state.integrationsByProvider[provider]) {
                Vue.set(state.integrationsByProvider, provider, {});
            }

            Vue.set(state.integrationsByProvider[provider], channel, list);
        },
        setIntegrationError (state, { provider, error }) {
            state.integrationError[provider] = error;
        },
        setJourneyGraph (state, { messageGraph }) {
            const journey = state.map[state.currentJourneyId];
            if (!journey) return;

            Vue.set(journey, 'messageGraph', messageGraph);
        },
        setIsLoadingMessages (state, isLoading) {
            state.loadingMessages = isLoading;
        },
        setStatus (state, { status }) {
            const journey = state.map[state.currentJourneyId];
            if (!journey) return;

            Vue.set(journey, 'status', status);
        },
        setJourneyAnalyticsAggData (state, { status, result = null }) {
            state.journeyAnalyticsAggData = {
                status,
                result,
            };
        },
    },
    actions: {
        async buildAndCreateJourney (
            { rootGetters, dispatch },
            { appId, name, target = null, segment = null, description = '' },
        ) {
            const app = rootGetters['apps/getAppById'](appId);

            let audience = [
                {
                    source: {
                        visitors: null,
                    },
                },
                {
                    filter: 'false',
                },
            ];
            let audienceUiHint = {
                filters: [
                    {
                        kind: 'visitor',
                        segmentId: 'noOne',
                    },
                ],
            };

            if (segment) {
                ({ audience, audienceUiHint } = await buildAudienceAndAudienceUiHint(segment, app));
            }

            const journey = await buildJourney({
                audience,
                audienceUiHint,
                app,
                name,
                description,
                target,
            });

            let createdJourney;
            try {
                createdJourney = await dispatch('journeys/createJourney', { journey }, { root: true });
            } catch (err) {
                throw err;
            }

            return createdJourney;
        },
        async cloneJourney ({ commit, dispatch, getters }, { journey, app, preventCodeBlocks }) {
            commit('setIsCloningJourney', { isCloningJourney: true });

            const isMessageGraphJourney = !!(journey.messageGraph && journey.messageGraph.nodes);

            let createdJourney;
            try {
                if (isMessageGraphJourney) {
                    const sanitizedGraph = sanitizeMessageGraph(journey.messageGraph);

                    const newJourney = await buildJourney({
                        audience: journey.audience,
                        audienceUiHint: journey.audienceUiHint,
                        app,
                        name: `Copy of ${journey.name}`,
                        target: journey.goal,
                    });

                    newJourney.isGoalOptional = getters.getIsGoalOptional;

                    createdJourney = await dispatch('createJourney', { journey: newJourney });
                    const originalMessages = extractMessagesFromMessageGraph(sanitizedGraph);
                    const clonedMessages = await dispatch('cloneMessagesFromMessageGraph', {
                        originalMessages,
                        preventCodeBlocks,
                        fallbackAppId: journey.appId,
                    });

                    const emailCreationPromises = [];
                    const emailIdMapping = {};

                    for (let i = 0; i < originalMessages.length; i++) {
                        const originalMessage = originalMessages[i];
                        const clonedMessage = clonedMessages[i];

                        if (originalMessage.type === EMAIL) {
                            const emailPromise = dispatch(
                                'emails/createOmnichannel',
                                {
                                    appId: clonedMessage.appId,
                                    attributes: { journeyId: createdJourney.id },
                                    channelType: 'email',
                                    guideId: clonedMessage.id,
                                    name: clonedMessage.name,
                                    provider: originalMessage.provider || AWS,
                                    schedule: {
                                        sendPeriodically: true,
                                    },
                                    emailType: getEmailType(clonedMessage),
                                },
                                { root: true },
                            ).then((email) => {
                                emailIdMapping[originalMessage.id] = email.id;

                                return email;
                            });
                            emailCreationPromises.push(emailPromise);
                        }
                    }

                    await Promise.all(emailCreationPromises);

                    const messageIdMapping = {};
                    for (let i = 0; i < originalMessages.length; i++) {
                        const originalMessage = originalMessages[i];
                        const clonedMessage = clonedMessages[i];

                        if (originalMessage.type === EMAIL) {
                            messageIdMapping[originalMessage.id] = emailIdMapping[originalMessage.id];
                        } else {
                            messageIdMapping[originalMessage.id] = clonedMessage.id;
                        }
                    }

                    const clonedMessageGraph = cloneMessageGraph(sanitizedGraph, messageIdMapping);

                    clonedMessageGraph.nodes.forEach((node) => {
                        if (
                            node.nodeType === BRANCHING_NODES_TYPES.MESSAGE &&
                            node.message &&
                            node.message.type === EMAIL
                        ) {
                            const originalMessageIndex = originalMessages.findIndex(
                                (message) =>
                                    message.id ===
                                    Object.keys(messageIdMapping).find(
                                        (key) => messageIdMapping[key] === node.message.id,
                                    ),
                            );
                            if (originalMessageIndex !== -1) {
                                const clonedGuide = clonedMessages[originalMessageIndex];
                                node.message.guideId = clonedGuide.id;
                            }
                        }
                    });

                    await dispatch('updateMessageGraph', {
                        journeyId: createdJourney.id,
                        messageGraph: sanitizeMessageGraph(clonedMessageGraph),
                    });

                    const clonedGuides = originalMessages
                        .map((msg, i) => (msg.type === GUIDE ? clonedMessages[i] : null))
                        .filter(Boolean);
                    await dispatch('linkClonedGuidesToJourney', { clonedGuides, journeyId: createdJourney.id });
                } else {
                    const hasNoGoal = getters.getEngagementConversion.itemType === ENGAGEMENT_GOAL_TYPES.NO_GOAL;
                    const target = hasNoGoal ? null : getters.getGoal;
                    const newJourney = await buildJourney({
                        audience: journey.audience,
                        audienceUiHint: journey.audienceUiHint,
                        app,
                        name: `Copy of ${journey.name}`,
                        target,
                    });

                    newJourney.engagements[0].isGoalOptional = getters.getIsGoalOptional;

                    createdJourney = await dispatch('createJourney', { journey: newJourney });
                    const clonedMessages = await dispatch('cloneMessagesFromJoureny', {
                        journey,
                        app,
                        preventCodeBlocks,
                    });
                    await dispatch('addBulkMessagesToEngagement', {
                        messages: clonedMessages,
                        durations: getters.getMessages.map((message) => message.durationInDays),
                        journeyId: createdJourney?.id,
                    });
                }

                await dispatch('loadOne', { id: createdJourney.id, reFetch: true });
            } catch (err) {
                throw err;
            } finally {
                commit('setIsCloningJourney', { isCloningJourney: false });
            }

            return createdJourney;
        },
        async createJourney ({ commit }, { journey }) {
            try {
                const createdJourney = await createJourney(journey);
                commit('addOrUpdateJourneyInMaps', { journey: createdJourney });

                return createdJourney;
            } catch (ex) {
                throw new Error(`create journey unsuccessful: ${ex}`);
            }
        },
        async linkClonedGuidesToJourney ({ dispatch }, { clonedGuides, journeyId }) {
            if (clonedGuides.length === 0) return;

            const results = await Promise.allSettled(
                clonedGuides.map((clonedGuide) =>
                    dispatch(
                        'guides/patchGuide',
                        {
                            guide: {
                                ...clonedGuide,
                                attributes: {
                                    ...(clonedGuide.attributes || {}),
                                    journeyId,
                                },
                            },
                            remoteGuide: clonedGuide,
                            fieldsToPatch: ['attributes'],
                        },
                        { root: true },
                    ),
                ),
            );

            const rejected = results
                .map((result, i) => (result.status === 'rejected' ? { guide: clonedGuides[i] } : null))
                .filter(Boolean);

            if (rejected.length > 0) {
                const rejectedNames = rejected.map(({ guide }) => guide.name || guide.id).join(', ');
                const err = new Error(
                    `${rejected.length} of ${clonedGuides.length} guide(s) could not be linked:\n` +
                    `${rejectedNames}\n`,
                );
                err.createdJourneyId = journeyId;
                throw err;
            }
        },
        async loadAllJourneys ({ commit, rootGetters }, { reFetch = false } = {}) {
            const hasOrchestrateProductLine = rootGetters['productLines/hasProductLinePurchased']('orchestrate');
            if (!hasOrchestrateProductLine) {
                return;
            }

            if (loadAllPromise && !reFetch) {
                await loadAllPromise;

                return;
            }

            try {
                commit('setMap', { journeysMap: {} });
                commit('setGetAllJourneysStatus', { getAllJourneysStatus: LOADING });
                loadAllPromise = getAllJourneys();
                const journeys = await loadAllPromise;
                const journeysMap = journeys.reduce((map, journey) => {
                    map[journey.id] = journey;

                    return map;
                }, {});
                const getAllJourneysStatus = journeys.length ? RESOLVED : EMPTY;
                commit('setGetAllJourneysStatus', { getAllJourneysStatus });
                commit('setMap', { journeysMap });
            } catch (err) {
                console.error(err);
                commit('setGetAllJourneysStatus', { getAllJourneysStatus: REJECTED });
            }
        },
        async loadOne ({ commit, state, dispatch }, { reFetch = false, id } = {}) {
            commit('setError', { error: null });
            if (state.map[id] && !reFetch) {
                dispatch('setActiveJourney', { id });

                return;
            }

            try {
                const journey = await getJourneyById(id);
                commit('addOrUpdateJourneyInMaps', { journey });
                dispatch('setActiveJourney', { id });
            } catch (error) {
                commit('setError', { error });
            }
        },
        async updateJourney ({ commit, dispatch }, { journey, reFetch = true }) {
            commit('setError', { error: null });
            commit('setSaving', { saving: true });

            try {
                const updatedJourney = await updateJourney(journey);
                commit('addOrUpdateJourneyInMaps', { journey: updatedJourney });

                if (reFetch) {
                    getAllGuidesIdsByJourney(journey).forEach((id) =>
                        dispatch('guides/loadOne', { id, reFetch }, { root: true }),
                    );
                }
            } catch (err) {
                console.error(err);
                commit('setError', { error: ERROR_MESSAGES.JOURNEY_UPDATE });
            }

            commit('setSaving', { saving: false });
        },
        async updateJourneyAudience ({ commit }, { id, audience, audienceUiHint, reachInactiveVisitors }) {
            commit('setError', { error: null });
            commit('setSaving', { saving: true });

            try {
                const updatedJourney = await updateJourneyAudience(id, audience, audienceUiHint, reachInactiveVisitors);
                commit('addOrUpdateJourneyInMaps', { journey: updatedJourney });
            } catch (err) {
                console.error(err);
                commit('setError', { error: ERROR_MESSAGES.JOURNEY_UPDATE });
            }

            commit('setSaving', { saving: false });
        },
        async updateSchedule ({ commit }, { id, scheduleData }) {
            commit('setError', { error: null });
            commit('setSaving', { saving: true });

            try {
                const updatedJourney = await updateSchedule(id, scheduleData);
                commit('addOrUpdateJourneyInMaps', { journey: updatedJourney });
            } catch (err) {
                console.error(err);
                commit('setError', { error: ERROR_MESSAGES.JOURNEY_UPDATE });
            }

            commit('setSaving', { saving: false });
        },
        async deleteJourney ({ commit, state, dispatch }, { id }) {
            const messagesToRemovePromises = [];
            const journey = state.map[id];

            for (const message of messagesGenerator(journey)) {
                if (message.type === GUIDE) {
                    messagesToRemovePromises.push(dispatch('guides/remove', { guide: message }, { root: true }));
                }

                if (message.type === EMAIL) {
                    messagesToRemovePromises.push(dispatch('emails/deleteEmail', { id: message.id }, { root: true }));
                }
            }

            try {
                await Promise.all(messagesToRemovePromises);
                await deleteJourney(id);
                commit('removeJourneysFromMaps', { id });
                dispatch('resetActiveJourney');
            } catch (ex) {
                throw new Error(`delete journey unsuccessful: ${ex}`);
            }
        },
        async addMessageToEngagement ({ commit, state, dispatch }, { journeyId, engagementId, message }) {
            try {
                commit('setEditing', { editing: true });
                journeyId = journeyId || state.currentJourneyId;
                engagementId = engagementId || state.currentEngagementId;

                if (!journeyId || !engagementId) return;

                const engagement = await addEngagementMessage(journeyId, engagementId, message);

                if (message.type === EMAIL) {
                    await dispatch('emails/loadOne', { id: message.id, setActive: true }, { root: true });
                }

                commit('updateEngagement', { engagement });
            } catch (ex) {
                throw new Error(`add message to journey unsuccessful: ${ex}`);
            } finally {
                commit('setEditing', { editing: false });
            }
        },
        async deleteMessageFromEngagement ({ commit, state }, { message }) {
            try {
                commit('setEditing', { editing: true });
                const journeyId = state.currentJourneyId;
                const engagementId = state.currentEngagementId;

                if (!journeyId || !engagementId) return;

                const newEngagement = await deleteEngagementMessage(journeyId, engagementId, message.id);

                if (message.type === EMAIL) {
                    commit('emails/removeEmail', { emailId: message.id }, { root: true });
                }

                commit('updateEngagement', { engagement: newEngagement });
            } catch (ex) {
                throw new Error(`Delete message from journey unsuccessful: ${ex}`);
            } finally {
                commit('setEditing', { editing: false });
            }
        },
        async updateMessageDuration ({ commit, state }, { journeyId, engagementId, message, durationInDays }) {
            try {
                commit('setEditing', { editing: true });
                journeyId = journeyId || state.currentJourneyId;
                engagementId = engagementId || state.currentEngagementId;
                const messageId = message.id;

                if (!journeyId || !engagementId || !messageId) return;

                const engagement = await updateMessageDuration(journeyId, engagementId, messageId, durationInDays);

                commit('updateEngagement', { engagement });
            } catch (ex) {
                throw new Error(`add message to journey unsuccessful: ${ex}`);
            } finally {
                commit('setEditing', { editing: false });
            }
        },
        setActiveJourney ({ commit, state, dispatch }, { id }) {
            commit('setError', { error: null });
            const journey = state.map[id];

            if (!journey) {
                commit('setError', { error: ERROR_MESSAGES.JOURNEY_NOT_FOUND });

                return;
            }

            dispatch('resetActiveJourney');
            commit('setCurrentJourneyId', { id });
            commit('setCurrentEngagementId', { engagementId: journey.engagements?.at(0)?.id });
        },
        resetActiveJourney ({ commit }) {
            commit('setCurrentJourneyId', { id: null });
            commit('setCurrentEngagementId', { engagementId: null });
            commit('setJourneySegmentVisitorCount', { count: 0 });
            commit('setCurrentMessageId', { messageId: null });
        },
        setActiveMessage ({ commit, getters, dispatch }, { messageId }) {
            commit('setCurrentMessageId', { messageId });

            const { type, guideId } = getters?.getActiveMessage || {};

            if (type === EMAIL || type === HUBSPOT || !messageId) {
                dispatch('emails/setActiveEmail', { id: messageId, guideId }, { root: true });
            }

            if (type === GUIDE || !messageId) {
                commit('guides/setActiveGuideById', { id: guideId }, { root: true });
            }
        },
        async cloneMessagesFromJoureny ({ dispatch, rootGetters }, { journey, app, preventCodeBlocks }) {
            const hasIntegratedEmails = journey.engagements[0].messages.some((message) => isIntegratedEmail(message));
            if (hasIntegratedEmails) {
                throw new Error('Cannot clone journey containing integrated emails');
            }

            const promises = journey.engagements[0].messages.map((message) => {
                if (message.type === EMAIL) {
                    return new Promise(async (resolve) => {
                        const email = rootGetters['emails/getEmailById'](message.id);
                        const clonedMessage = await dispatch(
                            'emails/cloneGuideByEmailId',
                            { id: message.id },
                            { root: true },
                        );
                        clonedMessage.description = email.description;
                        resolve(clonedMessage);
                    });
                }

                const guide = rootGetters['guides/getGuideById'](message.id);

                return dispatch('guides/cloneGuide', {
                    guide,
                    app,
                    options: { cloneCodeBlock: !preventCodeBlocks, isCloningIntoJourney: true },
                }, { root: true });
            });

            return Promise.all(promises);
        },
        async cloneMessagesFromMessageGraph ({ dispatch, rootGetters }, { originalMessages, preventCodeBlocks, fallbackAppId }) {
            // Check if any messages are integrated emails and throw an error if found
            const hasIntegratedEmails = originalMessages.some((message) => isIntegratedEmail(message));
            if (hasIntegratedEmails) {
                throw new Error('Cannot clone journey containing integrated emails');
            }

            const promises = originalMessages.map((message) => {
                if (message.type === EMAIL) {
                    return new Promise(async (resolve) => {
                        const email = rootGetters['emails/getEmailById'](message.id);
                        const clonedGuide = await dispatch(
                            'emails/cloneGuideByEmailId',
                            { id: message.id },
                            { root: true },
                        );
                        const clonedMessage = {
                            ...clonedGuide,
                            description: email.description,
                            guideId: clonedGuide.id,
                        };
                        resolve(clonedMessage);
                    });
                }

                const { id } = message;
                const appId = message.appId || fallbackAppId; // fallback to journey appId for template-created messages
                const guide = rootGetters['guides/getGuideById'](id);
                const app = rootGetters['apps/getAppById'](appId);

                return dispatch('guides/cloneGuide', {
                    guide,
                    app,
                    options: { cloneCodeBlock: !preventCodeBlocks, isCloningIntoJourney: true },
                }, { root: true });
            });

            return Promise.all(promises);
        },
        async createMessagesFromLayout ({ dispatch, getters, rootGetters }, { layout, app }) {
            const emptyTheme = {
                attributes: {
                    platform: 'web',
                },
            };
            const promises = [];
            layout.messages.forEach((message) => {
                const isEmail = message.type === EMAIL;
                promises.push(createGuideFromLayout(
                    message.layout,
                    {
                        app,
                        guideName: message.layout.name,
                        guideDescription: message.layout.description,
                        theme: emptyTheme,
                        isEmail,
                        isOrchestrate: true,
                        isOrchestrateTemplate: true,
                        maxPriority: rootGetters['guides/maxPriority'],
                        customLayoutTheme: rootGetters['themes/getThemeById'](message.layout?.themeId),
                        journeyId: getters.getActiveJourney?.id,
                    },
                ));
            });

            const guidesFromLayout = await Promise.all(promises);

            return Promise.all(guidesFromLayout.map(({ guide: guideFromLayout, isOrgResource, location }) => {
                return dispatch(
                    'guides/saveGuide',
                    { guide: guideFromLayout, isOrgResource, location },
                    { root: true },
                );
            }));
        },
        async addBulkMessagesToEngagement ({ dispatch, state }, { messages, durations, journeyId }) {
            const engagementId = state.map[journeyId].engagements[0].id;
            for (const message of messages) {
                message.attributes.journeyId = journeyId;
                const index = messages.indexOf(message);
                const { id, ...props } = message;
                const { isEmail } = message.attributes;
                const type = isEmail ? EMAIL : GUIDE;

                const payload = {
                    ...props,
                    id,
                    type,
                    ...(isEmail && { attributes: { journeyId }, provider: AWS }),
                    insertAtIndex: index,
                    guideId: id,
                };

                // eslint-disable-next-line no-await-in-loop
                await dispatch('addMessageToEngagement', { journeyId, engagementId, message: payload });

                if (isEmail && message.description) {
                    // eslint-disable-next-line no-await-in-loop
                    await dispatch(
                        'emails/updateOmniChannelField',
                        {
                            emailId: message.id,
                            field: 'description',
                            value: message.description,
                        },
                        { root: true },
                    );
                }
                const durationInDays = durations[index];
                // eslint-disable-next-line no-await-in-loop
                await dispatch('updateMessageDuration', {
                    journeyId,
                    engagementId,
                    message: { id: message.id },
                    durationInDays,
                });
            }
        },
        async createJourneyTemplate ({ commit, dispatch }, { selectedLayout, journeyId, app, theme }) {
            commit('setError', { error: null });
            commit('setIsCreatingTemplate', { isCreatingTemplate: true });

            try {
                if (selectedLayout.messageGraph) {
                    await dispatch('createJourneyFromMessageGraph', { selectedLayout, journeyId, app, theme });
                } else {
                    await dispatch('createJourneyFromLinearLayout', { selectedLayout, journeyId, app });
                }
            } catch (err) {
                commit('setError', {
                    error:
                        err.message === 'Cannot clone journey containing integrated emails'
                            ? err.message
                            : ERROR_MESSAGES.MISSING_MESSAGE,
                });
            } finally {
                commit('setIsCreatingTemplate', { isCreatingTemplate: false });
            }
        },

        async createJourneyFromMessageGraph ({ dispatch, state, getters }, { selectedLayout, journeyId, app, theme }) {
            const journey = state.map[journeyId];
            dispatch('setActiveJourney', { id: journeyId });

            const isNotAnEmptyJourney = getters.isNotAnEmptyJourney(journey);
            if (isNotAnEmptyJourney) return;

            if (selectedLayout.messageGraph) {
                selectedLayout.messageGraph = sanitizeMessageGraph(selectedLayout.messageGraph);
            }

            await dispatch('cleanJourney', { journey });

            // set the start node id in the selectedLayout from the journey
            // Initialize node ID mapping
            const nodeIdMap = initializeNodeIdMapping(selectedLayout, journey.messageGraph.startNodeId);
            // Update start node IDs
            setStartNodeIds(selectedLayout, journey.messageGraph.startNodeId);

            // Create guides and emails from message nodes
            const { emailNodes, regularNodes } = await dispatch('createMessagesForMessageNodes', {
                selectedLayout,
                journeyId,
                app,
                theme,
                nodeIdMap,
            });

            // Process regular guide nodes
            processRegularNodes(regularNodes);

            // Process email nodes
            await dispatch('processEmailNodes', { emailNodes, journeyId, app, theme });

            // Update condition nodes with new guide IDs
            updateConditionNodes(selectedLayout, regularNodes, emailNodes);

            // Create nodes and edges in the graph
            await dispatch('createGraphNodesAndEdges', {
                selectedLayout,
                journey,
                nodeIdMap,
            });
        },

        async createJourneyFromLinearLayout ({ dispatch }, { selectedLayout, journeyId, app }) {
            const templateMessages = await dispatch('createMessagesFromLayout', {
                layout: selectedLayout,
                app,
            });

            await dispatch('addBulkMessagesToEngagement', {
                messages: templateMessages,
                durations: selectedLayout.messages.map((message) => message.durationInDays),
                journeyId,
            });

            dispatch('setActiveMessage', { messageId: templateMessages[0].id });
        },

        async cleanJourney ({ dispatch }, { journey }) {
            // Delete the edge from start to exit in the empty journey
            await dispatch('graph/deleteEdge', {
                journeyId: journey.id,
                edgeId: journey.messageGraph.edges[0].id,
            });

            // Delete the exit node that is not a message node
            await dispatch('graph/deleteNode', {
                journeyId: journey.id,
                nodeId: journey.messageGraph.nodes[1].id,
            });
        },

        async createMessagesForMessageNodes ({ dispatch, getters, rootGetters }, { selectedLayout, app, theme }) {
            // Extract message nodes
            const messageNodes = selectedLayout.messageGraph.nodes.filter(
                (node) => node.nodeType === BRANCHING_NODES_TYPES.MESSAGE,
            );

            // Get journey message layouts
            const layouts = getJourneyMessagesForJourneyLayout(selectedLayout.id, theme, {
                platform: app.platform,
            });

            // Create guides for all message nodes
            const guidePromises = messageNodes.map(async (node) => {
                const layout = layouts.find((layout) => layout.type === node.messageData?.layout);
                const isEmail = layout?.messageType === EMAIL;

                const { guide: guideFromLayout, isOrgResource, location } = await createGuideFromLayout(
                    layout,
                    {
                        app,
                        guideName: node.messageData.name,
                        guideDescription: node.messageData.description,
                        theme,
                        isEmail,
                        isEmailTransactional: isEmailTransactional(layout),
                        isOrchestrate: true,
                        isOrchestrateTemplate: true,
                        maxPriority: rootGetters['guides/maxPriority'],
                        customLayoutTheme: rootGetters['themes/getThemeById'](layout?.themeId),
                        journeyId: getters.getActiveJourney?.id,
                    },
                );
                const guide = await dispatch(
                    'guides/saveGuide',
                    { guide: guideFromLayout, isOrgResource, location },
                    { root: true },
                );

                return {
                    nodeId: node.id,
                    guideId: guide.id,
                    messageNode: node,
                    layout,
                    isEmail,
                    ...isEmail && { isEmailTransactional: get(guide, 'attributes.isEmailTransactional', false) },
                };
            });

            const createdGuides = await Promise.all(guidePromises);

            // Separate email and regular nodes
            const emailNodes = [];
            const regularNodes = [];

            createdGuides.forEach((item) => {
                if (item.isEmail) {
                    emailNodes.push(item);
                } else {
                    regularNodes.push(item);
                }
            });

            return { emailNodes, regularNodes };
        },

        async processEmailNodes ({ dispatch, getters, rootGetters }, { emailNodes, journeyId, app, theme }) {
            const emailPromises = emailNodes.map(async ({ messageNode, layout, isEmailTransactional }) => {
                const { guide: guideFromLayout, isOrgResource, location } = await createGuideFromLayout(
                    layout,
                    {
                        app,
                        guideName: messageNode.messageData.name,
                        guideDescription: messageNode.messageData.description,
                        theme,
                        isEmail: true,
                        isEmailTransactional,
                        isOrchestrate: true,
                        isOrchestrateTemplate: true,
                        maxPriority: rootGetters['guides/maxPriority'],
                        customLayoutTheme: rootGetters['themes/getThemeById'](layout?.themeId),
                        journeyId: getters.getActiveJourney?.id,
                    },
                );
                const guide = await dispatch(
                    'guides/saveGuide',
                    { guide: guideFromLayout, isOrgResource, location },
                    { root: true },
                );

                const email = await dispatch(
                    'emails/createOmnichannel',
                    {
                        name: messageNode.messageData?.name,
                        description: messageNode.messageData?.description,
                        appId: app.id,
                        channelType: 'email',
                        guideId: guide.id,
                        provider: AWS,
                        schedule: {
                            sendPeriodically: true,
                        },
                        emailType: getEmailType(guide),
                    },
                    { root: true },
                );

                messageNode.messageData = {
                    provider: AWS,
                    type: EMAIL,
                    id: email.id,
                    layout,
                    durationInDays: messageNode.messageData?.durationInDays,
                    emailData: {
                        name: messageNode.messageData?.name,
                        guideId: guide.id,
                        emailType: email.emailType,
                        attributes: {
                            journeyId,
                        },
                    },
                };
            });

            await Promise.all(emailPromises);
        },

        async createGraphNodesAndEdges ({ dispatch, state }, { selectedLayout, journey, nodeIdMap }) {
            // Store the ORIGINAL template start node ID before any mutations (this is what edges reference)
            const originalTemplateStartNodeId = Object.keys(nodeIdMap).find(
                (key) => nodeIdMap[key] === journey.messageGraph.startNodeId,
            );
            // Filter out the start node since we can't delete it - find it by nodeType, not ID
            const nodesToAdd = selectedLayout.messageGraph.nodes.filter(
                (node) => node.nodeType !== BRANCHING_NODES_TYPES.START,
            );

            // Create nodes (backend will preserve the journey's original start node)
            await dispatch('graph/setGraphNodes', { nodes: nodesToAdd });

            // Update nodeIdMap with newly created nodes
            const updatedNodes = state.map[journey.id].messageGraph.nodes;

            // Find the journey's actual start node (should still be there with the original journey ID)
            let journeyStartNode = updatedNodes.find((node) => node.nodeType === BRANCHING_NODES_TYPES.START);

            // If not found in updated nodes, create reference to the journey's start node
            if (!journeyStartNode && journey.messageGraph.startNodeId) {
                journeyStartNode = {
                    id: journey.messageGraph.startNodeId,
                    nodeType: BRANCHING_NODES_TYPES.START,
                };
            }

            // Debug: Check what start node IDs we have
            const templateStartNode = selectedLayout.messageGraph.nodes.find(
                (node) => node.nodeType === BRANCHING_NODES_TYPES.START,
            );

            // Map template start node ID to journey's actual start node ID
            if (templateStartNode && journeyStartNode) {
                nodeIdMap[templateStartNode.id] = journeyStartNode.id;
            }

            // Also map the messageGraph.startNodeId if it's different
            if (selectedLayout.messageGraph.startNodeId && journeyStartNode) {
                nodeIdMap[selectedLayout.messageGraph.startNodeId] = journeyStartNode.id;
            }

            // CRITICAL: Map the original template start node ID that edges are actually using
            if (originalTemplateStartNodeId && journeyStartNode) {
                nodeIdMap[originalTemplateStartNodeId] = journeyStartNode.id;
            }

            // Also check edges and map any start node IDs they reference that we haven't mapped yet
            selectedLayout.messageGraph.edges.forEach((edge) => {
                if (edge.from && !nodeIdMap[edge.from] && journeyStartNode) {
                    // Check if this might be a start node by checking if any node has this ID
                    const isStartNodeReference = !selectedLayout.messageGraph.nodes.find(
                        (node) => node.id === edge.from,
                    );
                    if (isStartNodeReference) {
                        nodeIdMap[edge.from] = journeyStartNode.id;
                    }
                }
            });

            // Build mapping for other nodes (they'll be after the start node in the array)
            const nonStartNodes = updatedNodes.filter((node) => node.nodeType !== BRANCHING_NODES_TYPES.START);
            nodesToAdd.forEach((originalNode, index) => {
                nodeIdMap[originalNode.id] = nonStartNodes[index].id;
            });

            // Update edges with the complete nodeIdMap
            selectedLayout.messageGraph.edges.forEach((edge) => {
                if (nodeIdMap[edge.from]) {
                    edge.from = nodeIdMap[edge.from];
                }
                if (nodeIdMap[edge.to]) {
                    edge.to = nodeIdMap[edge.to];
                }
            });

            // Create edges
            await dispatch('graph/setGraphEdges', { edges: selectedLayout.messageGraph.edges });

            // Reload the journey to get the updated messageGraph with all nodes and edges
            await dispatch('loadOne', { id: journey.id, reFetch: true });
        },
        async loadIntegrationsByProvider ({ commit }, { provider, channel }) {
            commit('setIntegrationError', {
                provider,
                error: null,
            });
            try {
                let list = [];

                switch (channel) {
                    case CHANNEL_TYPES.EMAIL:
                        ({ data: list } = await loadEmailsByProvider(provider));
                        break;
                    default:
                        break;
                }

                commit('setIntegrationsByProvider', { provider, channel, list });
            } catch {
                commit('setIntegrationError', {
                    provider,
                    error: new Error(`Failed to load ${provider} ${channel}`),
                });
            }
        },
        setJourneyGraph ({ commit }, { messageGraph }) {
            commit('setJourneyGraph', { messageGraph });
        },
        async updateJourneyStatus ({ commit, dispatch, state }, { journeyId, status }) {
            commit('setError', { error: null });
            commit('setSaving', { saving: true });

            try {
                const journey = state.map[journeyId];
                if (journey?.messageGraph?.nodes && journey?.messageGraph?.edges) {
                    const sanitized = sanitizeMessageGraph(journey.messageGraph);
                    if (!isEqual(sanitized.nodes, journey.messageGraph.nodes) || !isEqual(sanitized.edges, journey.messageGraph.edges)) {
                        await dispatch('updateMessageGraph', { journeyId, messageGraph: sanitized });
                    }
                }
                await updateJourneyStatus(journeyId, status);
                commit('setStatus', { status });
            } catch (err) {
                console.error(err);
                commit('setError', { error: ERROR_MESSAGES.JOURNEY_STATUS_UPDATE });
            }

            commit('setSaving', { saving: false });
        },
        setIsLoadingMessages ({ commit }, isLoading) {
            commit('setIsLoadingMessages', isLoading);
        },
        async updateMessageGraph ({ commit, state }, { journeyId, messageGraph }) {
            commit('setError', { error: null });
            commit('setSaving', { saving: true });

            try {
                const updatedMessageGraph = await updateMessageGraph(journeyId || state.currentJourneyId, messageGraph);
                commit('setJourneyGraph', { messageGraph: updatedMessageGraph });

                return updatedMessageGraph;
            } catch (err) {
                commit('setError', { error: ERROR_MESSAGES.JOURNEY_UPDATE });
                throw err;
            } finally {
                commit('setSaving', { saving: false });
            }
        },
    },
    getters: {
        getIntegrationError: (state) => (provider) => {
            return state.integrationError[provider];
        },
        getError: (state) => state.error,
        getMap: (state) => state.map,
        list: (state) => Object.values(state.map),
        getJourneyById: (state) => (id) => state.map[id],
        getActiveJourney: (state, { getJourneyById }) => getJourneyById(state.currentJourneyId),
        getActiveEngagement: (state, { getActiveJourney }) =>
            getActiveJourney?.engagements?.find(({ id }) => id === state.currentEngagementId),
        getActiveMessage: (state, { getMessageById }) => {
            return getMessageById(state.currentMessageId);
        },
        getEmailSettings: (state, { getActiveJourney }) => getActiveJourney?.emailSettings,
        isEmailSettingsEmpty: (state, { getEmailSettings }) => !getEmailSettings || !getEmailSettings.identity,
        getEngagementConversion: (state, { getActiveEngagement }) =>
            getActiveEngagement?.goal ||
            (getActiveEngagement?.isGoalOptional ? { itemType: ENGAGEMENT_GOAL_TYPES.NO_GOAL } : {}),
        getGoal: (state, { getActiveJourney, getEngagementConversion }) =>
            (getActiveJourney?.isGoalOptional
                ? { itemType: ENGAGEMENT_GOAL_TYPES.NO_GOAL }
                : getActiveJourney?.goal || getEngagementConversion),
        getIsGoalOptional: (state, { getActiveEngagement, getActiveJourney }) =>
            getActiveEngagement?.isGoalOptional || getActiveJourney?.isGoalOptional || false,
        getJourneyStatus: (state, { getActiveJourney }) => getActiveJourney?.status,
        isLocalizationEnabled: (state, getters, rootState, rootGetters) => (appId) => {
            const app = rootGetters['apps/getAppById'](appId);
            const localizationEnabledForApp = get(app, 'applicationFlags.guideLocalization', false);
            const hasPurchasedGuidesLocalization = rootGetters['productLines/hasProductLinePurchased'](
                'guides',
                'localizationEnabled',
            );

            return localizationEnabledForApp && hasPurchasedGuidesLocalization;
        },
        getPromotionById: (state, getters, rootState, rootGetters) => {
            const getGuideById = rootGetters['guides/getGuideById'];

            return (id) => {
                const guide = getGuideById(id);

                if (!guide) return {};

                return {
                    ...guide,
                    guideId: guide.id,
                };
            };
        },
        getMessages: (state, { getActiveJourney, getActiveEngagement, getPromotionById }, rootState, rootGetters) => {
            const getEmailById = rootGetters['emails/getEmailById'];
            let { messages = [] } = getActiveEngagement || {};

            if (getActiveJourney.messageGraph) {
                messages = extractMessagesFromMessageGraph(getActiveJourney.messageGraph);
            }

            return messages.map((message) => {
                const entity = message.type === EMAIL ? getEmailById(message.id) : getPromotionById(message.id);
                const { guideId, name, description } = entity || {};
                const returnObject = {
                    ...message,
                    name,
                    guideId,
                    appId: message.appId || getActiveJourney?.appId, // fallback to journey appId when message doesn't have one (e.g., template-created messages)
                    description,
                };

                if (message.type === EMAIL) {
                    if (entity?.provider === AWS) {
                        returnObject.isEmptyEmailSettings = !entity?.emailSettings?.identity;
                    } else {
                        returnObject.isEmptyEmailSettings = false;
                    }
                }

                return returnObject;
            });
        },
        getConditions: (state, { getActiveJourney }) => {
            if (!getActiveJourney?.messageGraph) {
                return [];
            }

            const conditionNodes = getActiveJourney.messageGraph.nodes.filter(
                (node) => node.nodeType === BRANCHING_NODES_TYPES.CONDITION,
            );

            return Array.from(new Map(conditionNodes.map((node) => [node.id, node])).values());
        },
        getMessageById: (state, { getMessages }) => (id) => {
            return getMessages.find((message) => message.id === id);
        },
        getJourneySegmentVisitorCount: (state) => state.journeySegmentVisitorCount || 0,
        categorizedMessagesMenuOptions (state, getters, rootState, rootGetters) {
            return (
                MESSAGE_DROPDOWN_OPTIONS
                    /* category (For example: EMAIL, GUIDES, INTEGRATIONS, ACTIONS...) */
                    .filter((category) => shouldAddOptionToMessageDropdown(rootGetters, category))
                    .map((category, index) => ({
                        ...category,
                        ...(index > 0 ? { showDivider: true } : {}),
                    }))
                    /* items (For example: CREATE_GUIDE, CLONE_GUIDE, CREATE_EMAIL, CLONE_EMAIL, CONDITIONAL_SPLIT, CONNECT_TO_STEP, ADD_HUBSPOT_EMAIL, ADD_MARKETO_EMAIL...) */
                    .flatMap((category) => [category, ...category.items])
                    .filter((option) => option.category || shouldAddOptionToMessageDropdown(rootGetters, option))
                    .map((option) => {
                        if (option.category) {
                            return {
                                category: option.category,
                                showDivider: option.showDivider,
                            };
                        }

                        return {
                            ...option,
                            id: option.label,
                            disabled: getDropdownOptionsDisabled(rootGetters, option.disabledIf) || !!option.disabled,
                            description: getDropdownOptionsDescription(rootGetters, option.description),
                        };
                    })
            );
        },
        hasJourneysEnabled: (state, getters, rootState, rootGetters) => rootGetters['productLines/hasProductLinePurchased']('orchestrate'),
        getIntegrationsByProvider: (state) => (provider, channel) => {
            return state.integrationsByProvider[provider]?.[channel] || [];
        },
        getJourneyAnalyticsAggData: (state) => state.journeyAnalyticsAggData.result,
        getJourneyAnalyticsAggStatus: (state) => state.journeyAnalyticsAggData.status,
        isJourneyLaunched: (state, { getActiveJourney }) => {
            const launchedStatuses = [ACTIVE, COMPLETED, DISABLED, PAUSED];

            return launchedStatuses.includes(getActiveJourney?.status);
        },
        isBlockedByStatus: (state, { getJourneyStatus }) => {
            /*
                isJourneyLaunched and isBlockedByStatus are similar but we are going
                to add more logic to isBlockedByStatus (allowing edit some of the things
                when journey is paused) so keep it duplicated for now
            */
            const blocksEditingStatuses = [ACTIVE, COMPLETED, DISABLED, PAUSED];

            return blocksEditingStatuses.includes(getJourneyStatus);
        },
        isEditableWhenPaused: (state, { getJourneyStatus }, rootState, rootGetters) => {
            const hasOrchestrateEditLiveFlag = rootGetters['auth/hasSegmentFlag']('orchestrateEditLive');

            return hasOrchestrateEditLiveFlag && getJourneyStatus === PAUSED;
        },
        isNotAnEmptyJourney: () => (journey) => {
            // If the journey has more than one edge or two nodes, it is not an empty journey, and you shouldn't be here
            return journey.messageGraph.edges.length > 1 || journey.messageGraph.nodes.length > 2;
        },
        canEditAllJourneyMessages: (state, { getMessages }) => {
            return getMessages.every((message) => {
                const { appId, type } = message;

                switch (type) {
                    case GUIDE:
                        return canEditGuideByAppId({ appId });
                    case EMAIL:
                        return canUpdateEmail({ appId });
                    default:
                        return false;
                }
            });
        },
        canCloneAllJourneyMessages: (state, { getMessages }) => {
            return getMessages.every((message) => {
                const { appId, type } = message;

                switch (type) {
                    case GUIDE:
                        return canCreateGuideByAppId({ appId });
                    case EMAIL:
                        return canCreateEmail({ appId });
                    default:
                        return false;
                }
            });
        },
        canPublishAllJourneyMessages: (state, { getMessages }, rootState, rootGetters) => {
            return getMessages.every((message) => {
                const { id, appId, type } = message;

                switch (type) {
                    case GUIDE:
                        return canPublishGuide({ guide: rootGetters['guides/getGuideById'](id) });
                    case EMAIL:
                        return canPublishEmail({ appId });
                    default:
                        return false;
                }
            });
        },
        getJourneyRoute (state,{ getJourneyById }) {
            return ({ journeyId, messageId }) => {
                if (getJourneyById(journeyId)) {
                    return {
                        name: 'JourneyDetails',
                        params: { id: journeyId },
                        query: { view: 'map', messageId },
                    };
                }

                /* in this case journey not found means user has no pemission to this jouney - redirect to 403 error page */
                return { name: 'journeyForbidden', query: { messageId } };
            };
        },
    },
};

export default journeys;
